import { DollarSign } from "lucide-react";
import {
  Datagrid,
  DateInput,
  Edit,
  FunctionField,
  Labeled,
  List,
  NumberField,
  NumberInput,
  RadioButtonGroupInput,
  ReferenceField,
  SimpleForm,
  TextField,
  required,
  useNotify,
  useRedirect,
  useRefresh,
  useRecordContext,
} from "react-admin";
import { CardListActions, EditorToolbar } from "../../components";

const filters = [
  <NumberInput source="id" label="Id" alwaysOn key="id" />,
  <DateInput source="date" label="Dedication Date" alwaysOn key="date" />,
];

const PageDescription = () => (
  <div className="page-description">
    <h1>Dedication Payouts</h1>
    <p>
      This page displays all dedication payouts generated by the dedication
      program. Each payout includes details like the speaker, payout amount, and
      payout status.
    </p>
  </div>
);

const EditPageDescription = () => (
  <div className="page-description">
    <h1>Edit Dedication Payout</h1>
    <p>
      Payout records are generated by the backend. Use this page to update the
      payout status.
    </p>
  </div>
);

const SpeakerNameField = () => (
  <FunctionField
    render={(record) =>
      [record?.name_first, record?.name_last].filter(Boolean).join(" ")
    }
  />
);

function ListView(props) {
  return (
    <>
      <PageDescription />
      <List
        {...props}
        actions={<CardListActions exporter={true} />}
        filters={filters}
        perPage={50}
        sort={{ field: "created_at", order: "DESC" }}
        title="Dedication Payouts"
      >
        <Datagrid rowClick="edit">
          <TextField source="id" label="ID" />
          <ReferenceField
            source="speaker_id"
            reference="speakers"
            label="Speaker"
          >
            <SpeakerNameField />
          </ReferenceField>
          <NumberField
            source="amount"
            locales="en-US"
            options={{ style: "currency", currency: "USD" }}
            label="Payout Amount"
          />
          <TextField source="status" label="Payout Status" />
        </Datagrid>
      </List>
    </>
  );
}

const transformPayout = (data, { previousData } = {}) => ({
  ...(previousData || data),
  status: data.status,
});

function EditForm() {
  const record = useRecordContext();
  return (
    <SimpleForm toolbar={<EditorToolbar />}>
      <EditPageDescription />
      <Labeled label="ID:">
        <TextField source="id" />
      </Labeled>
      <Labeled label="Speaker Name:">
        <ReferenceField
          source="speaker_id"
          reference="speakers"
          label="Speaker"
        >
          <SpeakerNameField />
        </ReferenceField>
      </Labeled>

      <Labeled label="Payout Amount">
        <NumberField
          source="amount"
          locales="en-US"
          options={{ style: "currency", currency: "USD" }}
        />
      </Labeled>
      {record?.status !== "completed" ? (
        <RadioButtonGroupInput
          source="status"
          label="Payout Status"
          choices={[
            { id: "pending", name: "Pending" },
            { id: "completed", name: "Completed" },
            { id: "cancelled", name: "Cancelled" },
          ]}
          validate={[required()]}
        />
      ) : (
        <Labeled label="Payout Status:">
          <TextField source="status" />
        </Labeled>
      )}
      {/* <RadioButtonGroupInput
        source="status"
        label="Payout Status"
        choices={[
          { id: "pending", name: "Pending" },
          { id: "completed", name: "Completed" },
          { id: "cancelled", name: "Cancelled" },
        ]}
        validate={[required()]}
      /> */}
    </SimpleForm>
  );
}

function EditView(props) {
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();
  return (
    <Edit
      {...props}
      mutationMode="pessimistic"
      transform={transformPayout}
      mutationOptions={{
        onSuccess: () => {
          notify("Dedication payout updated successfully!", {
            type: "success",
          });
          refresh();
          redirect(false);
        },
        onError: (error) => {
          notify(`Error: ${error.message}`, { type: "error" });
        },
      }}
    >
      <EditForm />
    </Edit>
  );
}

export const DedicationPayouts = {
  category: "database",
  name: "payouts",
  icon: DollarSign,
  list: ListView,
  edit: EditView,
  options: { label: "Dedication Payouts" },
};
